import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { toast } from "sonner";
import { Send, Check, Undo2, X, Printer, Scale, Boxes, PackageCheck, RefreshCcw, Copy, Plus } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { resolveQuotationTemplate } from "../lib/quotationTemplates";

const money = (v) =>
    Number(v || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const num = (v, d = 2) =>
    Number(v || 0).toLocaleString(undefined, {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
    });

function lineStatus(line) {
    return line.availability_status || line.allocation_status || line.allocation?.status || "draft";
}

function lineSummary(line) {
    const status = lineStatus(line);

    if (status === "without_stock") return "Out of stock";
    if (status === "not_enough") return "Advanced PO required";
    if (status === "available_in_x_batches") return line.tonality_summary || line.allocation?.tonality_summary || "Available";
    if (status === "available") return "Available";

    return "Pending";
}

function canSeePrice(user) {
    // Internal staff who work the organic quotation flow all have pricing visibility
    // (Sales Rep, Partner, Cashier, Operations Head, Branch OIC, Branch Manager, plus
    // Inquiry / Treasury / Accounting who validate & approve). Clients never log in, so
    // hiding the total from these roles only hid it from the people who need it — which is
    // why the SQ total showed on the print-out but not in the SQ module.
    const INTERNAL = [
        "admin", "inquiry_dept", "sales_agent", "branch_manager", "owner",
        "partner", "operations_head", "branch_oic", "cashier",
        "treasury", "accounting", "purchasing",
    ];
    return INTERNAL.includes(user?.role);
}

export function QuotationsList() {
    const [rows, setRows] = useState([]);

    useEffect(() => {
        (async () => {
            const { data } = await api.get("/quotations");
            setRows(data);
        })();
    }, []);

    return (
        <div className="p-3 md:p-6 space-y-4" data-testid="quotations-list-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="sc-section-title">Sales</div>
                    <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Quotations</h1>
                </div>

                <Link
                    to="/cart"
                    className="h-9 inline-flex items-center justify-center px-3 rounded-md border border-border text-sm hover:bg-secondary"
                    data-testid="goto-cart"
                >
                    Build new from Cart
                </Link>
            </div>

            <div className="sc-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[860px]">
                        <thead>
                            <tr>
                                <th>Doc no</th>
                                <th>Customer</th>
                                <th>Created by</th>
                                <th>Status</th>
                                <th className="text-right">Total Weight</th>
                                <th className="text-right">Boxes</th>
                                <th>Created</th>
                            </tr>
                        </thead>

                        <tbody>
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="text-center py-8 text-muted-foreground">
                                        No quotations yet.
                                    </td>
                                </tr>
                            )}

                            {rows.map((q) => {
                                const totalWeight = q.total_weight ?? (q.lines || []).reduce((a, l) => a + Number(l.total_weight || 0), 0);
                                const boxes = q.estimated_boxes ?? (q.lines || []).reduce((a, l) => a + Number(l.estimated_boxes || 0), 0);

                                return (
                                    <tr
                                        key={q.id}
                                        className="cursor-pointer"
                                        onClick={() => window.location.assign(`/quotations/${q.id}`)}
                                        data-testid={`quotation-row-${q.id}`}
                                    >
                                        <td className="font-mono">{q.doc_no}</td>
                                        <td>{q.customer_name}</td>
                                        <td className="text-muted-foreground">{q.user_email}</td>
                                        <td><StatusBadge status={q.status} /></td>
                                        <td className="sc-num">{num(totalWeight, 2)} kg</td>
                                        <td className="sc-num">{num(boxes, 2)}</td>
                                        <td className="text-muted-foreground">{new Date(q.created_at).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export function QuotationDetail() {
    const { id } = useParams();
    const [q, setQ] = useState(null);
    const [busy, setBusy] = useState(false);
    const [comment, setComment] = useState("");
    const { user, hasPermission } = useAuth();
    const nav = useNavigate();
    const revertToCart = async () => {
        if (!window.confirm("Revert this quotation back to the Sales Cart? Its lines return to your cart so you can edit the client / lines, and this quotation is closed (kept for audit).")) return;
        try {
            await api.post(`/quotations/${id}/revert-to-cart`);
            toast.success("Reverted to Sales Cart");
            nav("/cart");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const showPrice = canSeePrice(user);
    const isOwner = q && (q.user_id === user?.id || q.user_email === user?.email);
    const canEditDoc = q && ["draft", "for_revision", "rejected"].includes(q.status) && hasPermission("quotation.write") && (isOwner || user?.role === "admin");
    const showGiven = showPrice || canEditDoc;
    const isBMOH = ["admin", "branch_manager", "owner", "partner", "operations_head", "branch_oic"].includes(user?.role);
    const subtotalSpan = 8 + (showGiven ? 5 : 0);
    const isClientDoc = q && (q.doc_kind === "client" || q.hide_internal_pricing);

    const load = async () => {
        const { data } = await api.get(`/quotations/${id}`);
        setQ(data);
    };

    useEffect(() => {
        load();
    }, [id]);

    const action = async (path, body = {}, msg = "") => {
        setBusy(true);

        try {
            await api.post(`/quotations/${id}/${path}`, body);
            if (msg) toast.success(msg);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const updateLineQuantity = async (lineId, value) => {
        const quantity = Number(value);
        if (!Number.isFinite(quantity) || quantity <= 0) {
            toast.error("Invalid quantity");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.put(`/quotations/${id}/lines/${lineId}`, { quantity });
            setQ(data);
            toast.success("Quantity updated. Revalidate availability before submission.");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const revalidateLine = async (lineId) => {
        setBusy(true);
        try {
            const { data } = await api.post(`/quotations/${id}/lines/${lineId}/revalidate`);
            setQ(data);
            toast.success("Line availability revalidated");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    // SA-2: edit any line field (given price, area, description, size) without touching quantity.
    const updateLine = async (lineId, patch) => {
        setBusy(true);
        try {
            const { data } = await api.put(`/quotations/${id}/lines/${lineId}`, patch);
            setQ(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const saveHeader = async (patch) => {
        setBusy(true);
        try {
            const { data } = await api.put(`/quotations/${id}/header`, patch);
            setQ(data);
            toast.success("Saved");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    // SA-2: create the separate client-facing copy ("Quotation 2") with internal pricing stripped.
    const createClientCopy = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/quotations/${id}/client-copy`);
            toast.success(`Client copy ${data.doc_no} created`);
            nav(`/quotations/${data.id}`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const totals = useMemo(() => {
        const lines = q?.lines || [];

        return {
            qty: lines.reduce((a, l) => a + Number(l.quantity || 0), 0),
            weight: q?.total_weight ?? lines.reduce((a, l) => a + Number(l.total_weight || 0), 0),
            boxes: q?.estimated_boxes ?? lines.reduce((a, l) => a + Number(l.estimated_boxes || 0), 0),
            amount: q?.subtotal ?? lines.reduce((a, l) => a + Number(l.net_total || 0), 0),
            advancedPO: lines.filter((l) => l.advanced_po_allowed || lineStatus(l) === "not_enough" || lineStatus(l) === "without_stock").length,
        };
    }, [q]);

    if (!q) return <div className="p-6">Loading…</div>;

    return (
        <div className="p-3 md:p-6 space-y-5 md:space-y-6" data-testid="quotation-detail-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="sc-section-title">Quotation</div>
                    <h1 className="text-xl md:text-2xl font-semibold tracking-tight font-mono">{q.doc_no}</h1>
                    <div className="text-sm text-muted-foreground mt-1">
                        {q.customer_name} · {q.customer_code}
                        {q.source_quotation_id && (
                            <> · <Link to={`/quotations/${q.source_quotation_id}`} className="underline">from {q.source_doc_no || "internal quotation"}</Link></>
                        )}
                    </div>

                    <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                        <div>
                            <div className="text-xs text-muted-foreground uppercase tracking-wider">Transaction Type</div>
                            <div className="font-medium">
                                {q.transaction_type === "inventory_replenishment" ? "Inventory Replenishment" : "Customer Sale"}
                            </div>
                        </div>

                        <div>
                            <div className="text-xs text-muted-foreground uppercase tracking-wider">Business Mode</div>
                            <div className="font-medium">{q.business_mode || "organic"}</div>
                        </div>

                        <div>
                            <div className="text-xs text-muted-foreground uppercase tracking-wider">Company / Branch</div>
                            <div className="font-medium">
                                {q.company || "—"} {q.branch ? `• ${q.branch}` : ""}
                            </div>
                        </div>
                    </div>

                    <div className="mt-3 rounded-md border border-border bg-secondary/30 p-3">
                        <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Customer Details</div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                            <div>
                                <div className="text-xs text-muted-foreground">Company Name</div>
                                <div className="font-medium">{q.customer_snapshot?.company_name || q.customer_name || "—"}</div>
                            </div>
                            <div>
                                <div className="text-xs text-muted-foreground">TIN</div>
                                <div className="font-medium">{q.customer_snapshot?.tin || q.customer_tin || "—"}</div>
                            </div>
                            <div>
                                <div className="text-xs text-muted-foreground">Contact Person</div>
                                <div className="font-medium">{q.customer_snapshot?.contact_person || q.customer_contact_person || "—"}</div>
                            </div>
                            <div>
                                <div className="text-xs text-muted-foreground">Contact No.</div>
                                <div className="font-medium">{q.customer_snapshot?.contact_no || q.customer_contact_no || "—"}</div>
                            </div>
                            <div>
                                <div className="text-xs text-muted-foreground">Email Address</div>
                                <div className="font-medium">{q.customer_snapshot?.email || q.customer_email || "—"}</div>
                            </div>
                            <div>
                                <div className="text-xs text-muted-foreground">Address</div>
                                <div className="font-medium">{q.customer_snapshot?.address || q.customer_address || "—"}</div>
                            </div>
                        </div>
                    </div>

                    {q.transaction_type === "inventory_replenishment" && (
                        <div className="mt-3 rounded-md border border-border bg-secondary/30 p-3">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                                <div>
                                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Source Schema</div>
                                    <div className="font-mono text-xs mt-1">{q.source_schema_name || "—"}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Destination Schema</div>
                                    <div className="font-mono text-xs mt-1">{q.destination_schema_name || "—"}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Destination Warehouse</div>
                                    <div className="font-mono text-xs mt-1">{q.destination_warehouse_code || "—"}</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded border ${isClientDoc ? "bg-sky-100 text-sky-700 border-sky-200" : "bg-slate-100 text-slate-700 border-slate-200"}`}>
                        {isClientDoc ? "Client · Quotation 2" : "Internal"}
                    </span>
                    <StatusBadge status={q.status} />
                    {!isClientDoc && (
                        <button
                            onClick={createClientCopy}
                            disabled={busy}
                            className="h-9 inline-flex items-center gap-1 px-3 rounded-md border border-border text-sm hover:bg-secondary disabled:opacity-50"
                            data-testid="quotation-client-copy"
                            title="Create a client-facing copy (Quotation 2) with internal pricing hidden"
                        >
                            <Copy className="w-4 h-4" />
                            Client copy
                        </button>
                    )}
                    <button
                        onClick={() => nav(`/quotations/${id}/print`)}
                        className="h-9 inline-flex items-center gap-1 px-3 rounded-md border border-border text-sm hover:bg-secondary"
                        data-testid="quotation-print"
                    >
                        <Printer className="w-4 h-4" />
                        Print
                    </button>
                </div>
            </div>

            <div className={`grid grid-cols-2 ${showPrice ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-3`}>
                <SummaryCard icon={PackageCheck} label="Total Qty" value={num(totals.qty, 0)} />
                <SummaryCard icon={Scale} label="Total Weight" value={`${num(totals.weight, 2)} kg`} />
                <SummaryCard icon={Boxes} label="Estimated Boxes" value={num(totals.boxes, 2)} />
                <SummaryCard label="Advanced PO Lines" value={num(totals.advancedPO, 0)} />
                {showPrice && <SummaryCard label="Subtotal" value={`₱${money(totals.amount)}`} />}
            </div>

            <div className="sc-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[1100px]">
                        <thead>
                            <tr>
                                <th>Item</th>
                                <th>Area</th>
                                <th>Schema / Wh</th>
                                <th className="text-right">Qty</th>
                                <th className="text-right">Boxes</th>
                                <th className="text-right">Weight</th>
                                <th>Status</th>
                                <th>Advanced PO</th>
                                {showGiven && <th className="text-right">SRP Price</th>}
                                {showGiven && <th className="text-right">Disc %</th>}
                                {showGiven && <th className="text-right">Discounted Price</th>}
                                {showGiven && <th className="text-right">Special Disc</th>}
                                {showGiven && <th className="text-right">Cutting (SQ2)</th>}
                                {showPrice && <th className="text-right">Net</th>}
                            </tr>
                        </thead>

                        <tbody>
                            {q.lines.map((l) => (
                                <tr key={l.id}>
                                    <td>
                                        <div className="font-mono text-xs">{l.item_code}</div>
                                        {isClientDoc && canEditDoc ? (
                                            <input
                                                defaultValue={l.item_name || ""}
                                                onBlur={(e) => updateLine(l.id || l.line_id, { item_name: e.target.value })}
                                                disabled={busy}
                                                placeholder="Client item name"
                                                title="Client-copy item name — does not change the SAP item"
                                                className="sc-input h-6 text-[11px] mt-0.5 w-40"
                                                data-testid={`quotation-line-name-${l.id || l.line_id}`}
                                            />
                                        ) : (
                                            <div className="text-xs text-muted-foreground">{l.item_name}</div>
                                        )}
                                        {(l.for_cutting || isClientDoc) && canEditDoc ? (
                                            <input
                                                defaultValue={l.size || ""}
                                                onBlur={(e) => updateLine(l.id || l.line_id, { size: e.target.value })}
                                                disabled={busy}
                                                placeholder={isClientDoc ? "Client size (e.g. 30x30)" : "Cut size (SQ2)"}
                                                title={isClientDoc ? "Client-copy size — does not change the SAP item" : "Editable size for cutting (SQ2)"}
                                                className="sc-input h-6 text-[11px] mt-1 w-32"
                                                data-testid={`quotation-line-size-${l.id || l.line_id}`}
                                            />
                                        ) : (
                                            <div className="text-[11px] text-muted-foreground mt-1">{l.size || "—"}</div>
                                        )}
                                    </td>

                                    <td>
                                        {canEditDoc ? (
                                            <input
                                                defaultValue={l.area || ""}
                                                onBlur={(e) => updateLine(l.id || l.line_id, { area: e.target.value })}
                                                disabled={busy}
                                                placeholder="Area"
                                                className="sc-input h-8 text-xs w-28"
                                                data-testid={`quotation-line-area-${l.id || l.line_id}`}
                                            />
                                        ) : (
                                            <span className="text-xs text-muted-foreground">{l.area || "—"}</span>
                                        )}
                                    </td>

                                    <td>
                                        <div className="font-mono text-xs">{l.schema_name || "—"}</div>
                                        <div className="font-mono text-xs text-muted-foreground">{l.warehouse_code}</div>
                                    </td>

                                    <td>
                                        <div className="flex items-center justify-end gap-2">
                                            <input
                                                type="number"
                                                min="1"
                                                step="1"
                                                defaultValue={Number(l.quantity || 0)}
                                                onBlur={(e) => updateLineQuantity(l.id || l.line_id, e.target.value)}
                                                disabled={busy || !["draft", "for_revision", "rejected"].includes(q.status)}
                                                className="sc-input h-8 text-right font-mono text-xs w-24"
                                                data-testid={`quotation-line-quantity-${l.id || l.line_id}`}
                                            />
                                            <button
                                                onClick={() => revalidateLine(l.id || l.line_id)}
                                                disabled={busy || !["draft", "for_revision", "rejected"].includes(q.status)}
                                                className="h-8 px-2 rounded border border-border text-xs hover:bg-secondary disabled:opacity-50"
                                                title="Revalidate inventory"
                                            >
                                                <RefreshCcw className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                    <td className="sc-num">{num(l.estimated_boxes, 2)}</td>
                                    <td className="sc-num">{num(l.total_weight, 2)} {l.weight_unit || "kg"}</td>

                                    <td>
                                        <StatusBadge status={lineStatus(l)} />
                                        <div className="text-[11px] text-muted-foreground mt-1">
                                            {lineSummary(l)}
                                        </div>
                                    </td>

                                    <td className="text-xs text-muted-foreground">
                                        {l.advanced_po_allowed || lineStatus(l) === "not_enough" || lineStatus(l) === "without_stock"
                                            ? "Required / pending Purchasing review"
                                            : "—"}
                                    </td>

                                    {showGiven && (
                                        <td className="sc-num">
                                            {canEditDoc ? (
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    defaultValue={Number(l.given_price ?? l.unit_price ?? 0)}
                                                    onBlur={(e) => updateLine(l.id || l.line_id, { given_price: e.target.value })}
                                                    disabled={busy}
                                                    className="sc-input h-8 text-right font-mono text-xs w-28"
                                                    data-testid={`quotation-line-given-${l.id || l.line_id}`}
                                                />
                                            ) : (
                                                <span>₱{money(l.given_price ?? l.unit_price)}</span>
                                            )}
                                        </td>
                                    )}
                                    {showGiven && (
                                        <td className="sc-num">
                                            {canEditDoc ? (
                                                <div className="inline-flex items-center gap-1 justify-end">
                                                    <select
                                                        value={l.discount_mode === "amount" ? "amount" : "pct"}
                                                        disabled={busy}
                                                        onChange={(e) => updateLine(l.id || l.line_id, { discount_mode: e.target.value })}
                                                        className="sc-input h-8 text-xs w-12"
                                                        title="Discount as percent, or a fixed peso amount per unit"
                                                    >
                                                        <option value="pct">%</option>
                                                        <option value="amount">₱</option>
                                                    </select>
                                                    {l.discount_mode === "amount" ? (
                                                        <input
                                                            key={`amt-${l.id || l.line_id}`}
                                                            type="number" min="0" step="0.01"
                                                            defaultValue={Number(l.discount_amount || 0)}
                                                            onBlur={(e) => updateLine(l.id || l.line_id, { discount_amount: e.target.value })}
                                                            disabled={busy}
                                                            className="sc-input h-8 text-right font-mono text-xs w-20"
                                                            data-testid={`quotation-line-disc-amt-${l.id || l.line_id}`}
                                                        />
                                                    ) : (
                                                        <input
                                                            key={`pct-${l.id || l.line_id}`}
                                                            type="number" min="0" max="100" step="0.5"
                                                            defaultValue={Number(l.discount_pct || 0)}
                                                            onBlur={(e) => updateLine(l.id || l.line_id, { discount_pct: e.target.value })}
                                                            disabled={busy}
                                                            className="sc-input h-8 text-right font-mono text-xs w-20"
                                                            data-testid={`quotation-line-disc-${l.id || l.line_id}`}
                                                        />
                                                    )}
                                                </div>
                                            ) : (
                                                <span>{l.discount_mode === "amount" ? `₱${num(l.discount_amount || 0, 2)}/pc` : `${num(l.discount_pct || 0, 1)}%`}</span>
                                            )}
                                        </td>
                                    )}
                                    {showGiven && (
                                        <td className="sc-num">₱{money(l.discounted_price ?? (Number(l.given_price ?? l.unit_price ?? 0) * (1 - Number(l.discount_pct || 0) / 100)))}</td>
                                    )}
                                    {showGiven && (
                                        <td className="sc-num">
                                            {canEditDoc ? (
                                                <div className="inline-flex items-center gap-1 justify-end">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!l.special_discount_on}
                                                        disabled={busy}
                                                        onChange={(e) => updateLine(l.id || l.line_id, { special_discount_on: e.target.checked })}
                                                        title="Toggle additional / special discount"
                                                        data-testid={`quotation-line-special-on-${l.id || l.line_id}`}
                                                    />
{l.special_discount_on && (
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        step="0.5"
                                                        defaultValue={Number(l.special_discount_pct || 0)}
                                                        onBlur={(e) => updateLine(l.id || l.line_id, { special_discount_pct: e.target.value })}
                                                        disabled={busy}
                                                        className="sc-input h-8 text-right font-mono text-xs w-16"
                                                        data-testid={`quotation-line-special-${l.id || l.line_id}`}
                                                    />
                                                    )}
                                                </div>
                                            ) : (
                                                <span>{l.special_discount_on ? `${num(l.special_discount_pct || 0, 1)}%` : "—"}</span>
                                            )}
                                        </td>
                                    )}
                                    {showGiven && (
                                        <td className="sc-num">
                                            {canEditDoc ? (
                                                <div className="inline-flex items-center gap-1 justify-end">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!l.for_cutting}
                                                        disabled={busy}
                                                        onChange={(e) => updateLine(l.id || l.line_id, { for_cutting: e.target.checked })}
                                                        title="Mark for cutting (SQ1 → SQ2)"
                                                        data-testid={`quotation-line-cut-on-${l.id || l.line_id}`}
                                                    />
                                                    {l.for_cutting && (isBMOH ? (
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step="0.01"
                                                            defaultValue={Number(l.cut_price || 0)}
                                                            onBlur={(e) => updateLine(l.id || l.line_id, { cut_price: e.target.value })}
                                                            disabled={busy}
                                                            placeholder="SQ2 ₱"
                                                            className="sc-input h-8 text-right font-mono text-xs w-24"
                                                            data-testid={`quotation-line-cutprice-${l.id || l.line_id}`}
                                                        />
                                                    ) : (
                                                        <span className="text-[11px] text-muted-foreground whitespace-nowrap">{l.cut_price ? `₱${money(l.cut_price)}` : "BM/OH price"}</span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-xs">{l.for_cutting ? (l.cut_price ? `SQ2 ₱${money(l.cut_price)}` : "SQ2") : "—"}</span>
                                            )}
                                        </td>
                                    )}
                                    {showPrice && (
                                        <td className="sc-num">₱{money(l.net_total)}</td>
                                    )}
                                </tr>
                            ))}

                            {showPrice && (
                                <>
                                    <tr className="bg-secondary/20">
                                        <td colSpan={subtotalSpan} className="text-right text-xs uppercase tracking-wider text-muted-foreground font-medium">
                                            Subtotal
                                        </td>
                                        <td className="sc-num font-semibold">₱{money(q.subtotal)}</td>
                                    </tr>
                                    {(q.other_charges || []).map((c, i) => (
                                        <tr key={`oc-${i}`}>
                                            <td colSpan={subtotalSpan} className="text-right text-xs text-muted-foreground">{c.label}</td>
                                            <td className="sc-num">₱{money(c.amount)}</td>
                                        </tr>
                                    ))}
                                    <tr className="bg-secondary/40">
                                        <td colSpan={subtotalSpan} className="text-right text-xs uppercase tracking-wider font-semibold">
                                            Grand Total
                                        </td>
                                        <td className="sc-num font-semibold">₱{money(q.grand_total ?? q.subtotal)}</td>
                                    </tr>
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <ProjectChargesCard q={q} editable={canEditDoc} onSave={saveHeader} />

            <TermsSection
                q={q}
                editable={["draft", "for_revision", "rejected"].includes(q.status) && hasPermission("quotation.write")}
                onSaved={load}
            />

            <div className="grid lg:grid-cols-3 gap-4">
                {user?.role === "admin" && (
                    <div className="sc-card p-4 lg:col-span-2">
                        <div className="sc-section-title mb-2">Internal Status Timeline</div>
                        <ul className="space-y-2">
                            {(q.status_history || []).map((h, i) => (
                                <li key={i} className="text-sm flex items-center gap-3">
                                    <span className="text-xs text-muted-foreground font-mono w-44">
                                        {new Date(h.at).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}
                                    </span>
                                    <StatusBadge status={h.status} />
                                    <span className="text-muted-foreground">by {h.by}</span>
                                    {h.comment && <span className="text-muted-foreground italic">— {h.comment}</span>}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className={`sc-card p-4 space-y-3 ${user?.role === "admin" ? "" : "lg:col-span-3"}`}>
                    <div className="sc-section-title">Workflow actions</div>

                    <input
                        className="sc-input"
                        placeholder="Optional comment"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        data-testid="quotation-comment"
                    />

                    {(
                        q.status === "draft" ||
                        q.status === "for_revision" ||
                        q.status === "rejected"
                    ) && (
                        <button
                            onClick={() => action("submit", {}, "Submitted for approval")}
                            disabled={busy}
                            className="h-9 w-full inline-flex items-center justify-center gap-2 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 disabled:opacity-50"
                            data-testid="quotation-submit"
                        >
                            <Send className="w-4 h-4" />
                            Submit for approval
                        </button>
                    )}

                    {(q.status === "draft" || q.status === "for_revision" || q.status === "rejected") && (
                        <button
                            onClick={revertToCart}
                            disabled={busy}
                            className="h-9 w-full inline-flex items-center justify-center gap-2 rounded-md border border-border text-sm hover:bg-secondary disabled:opacity-50"
                            data-testid="quotation-revert-to-cart"
                        >
                            Revert to Sales Cart
                        </button>
                    )}

                    {q.status === "pending_approval" && hasPermission("quotation.approve") && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <button
                                onClick={() => action("approve", { action: "approve", comment })}
                                className="h-9 inline-flex items-center justify-center gap-1 rounded-md bg-emerald-600 text-white text-sm hover:opacity-90"
                                data-testid="quotation-approve"
                            >
                                <Check className="w-4 h-4" />
                                Approve
                            </button>

                            <button
                                onClick={() => action("approve", { action: "revise", comment })}
                                className="h-9 inline-flex items-center justify-center gap-1 rounded-md border border-border text-sm hover:bg-secondary"
                                data-testid="quotation-revise"
                            >
                                <Undo2 className="w-4 h-4" />
                                Revise
                            </button>

                            <button
                                onClick={() => action("approve", { action: "reject", comment })}
                                className="h-9 inline-flex items-center justify-center gap-1 rounded-md bg-red-600 text-white text-sm hover:opacity-90"
                                data-testid="quotation-reject"
                            >
                                <X className="w-4 h-4" />
                                Reject
                            </button>
                        </div>
                    )}

                    {(q.status === "approved" || q.status === "posted_to_sap") && hasPermission("sales_order.write") && (
                        <button
                            onClick={async () => {
                                try {
                                    const { data } = await api.post(`/sales-orders/from-quotation/${id}`);
                                    toast.success(`SO ${data.doc_no} created`);
                                    nav(`/sales-orders`);
                                } catch (e) {
                                    toast.error(formatApiError(e));
                                }
                            }}
                            className="h-9 w-full inline-flex items-center justify-center gap-2 rounded-md border border-border text-sm hover:bg-secondary"
                            data-testid="quotation-create-so"
                        >
                            Create Sales Order
                        </button>
                    )}

                    <div className="text-xs text-muted-foreground pt-2 border-t border-border">
                        Revision: {q.revision || 0} of 3 · Payment terms: {q.payment_terms || "—"}
                    </div>
                </div>
            </div>
        </div>
    );
}

// SA-2: header-level project fields + editable "other charges" (delivery, installation,
// cutting, pallet, rental, storage, warehouse …) that roll into the grand total.
function ProjectChargesCard({ q, editable, onSave }) {
    const [projectName, setProjectName] = useState(q.project_name || "");
    const [projectAddress, setProjectAddress] = useState(q.project_address || "");
    const [charges, setCharges] = useState(q.other_charges || []);
    const [payFullyPaid, setPayFullyPaid] = useState(q.pay_fully_paid ?? "");
    const [payPartial, setPayPartial] = useState(q.pay_partial ?? "");
    const [payBalance, setPayBalance] = useState(q.pay_balance ?? "");
    const [payTentative, setPayTentative] = useState(q.pay_tentative_date || "");
    const [reminders, setReminders] = useState(q.reminders_override || []);
    const nameLocked = !!(q.project_name && q.project_name.trim());

    useEffect(() => {
        setProjectName(q.project_name || "");
        setProjectAddress(q.project_address || "");
        setCharges(q.other_charges || []);
        setPayFullyPaid(q.pay_fully_paid ?? "");
        setPayPartial(q.pay_partial ?? "");
        setPayBalance(q.pay_balance ?? "");
        setPayTentative(q.pay_tentative_date || "");
        setReminders(q.reminders_override || []);
    }, [q.id, q.updated_at]);

    const addCharge = () => setCharges((c) => [...c, { label: "", amount: 0 }]);
    const setCharge = (i, patch) => setCharges((c) => c.map((row, j) => (j === i ? { ...row, ...patch } : row)));
    const removeCharge = (i) => setCharges((c) => c.filter((_, j) => j !== i));

    const save = () => {
        const payload = {
            project_address: projectAddress,
            other_charges: charges
                .filter((c) => (c.label || "").trim())
                .map((c) => ({ label: c.label, amount: Number(c.amount || 0) })),
        };
        if (!nameLocked && projectName.trim()) payload.project_name = projectName.trim();
        payload.pay_fully_paid = payFullyPaid === "" ? "" : Number(payFullyPaid || 0);
        payload.pay_partial = payPartial === "" ? "" : Number(payPartial || 0);
        payload.pay_balance = payBalance === "" ? "" : Number(payBalance || 0);
        payload.pay_tentative_date = payTentative || "";
        payload.reminders_override = reminders.filter((r) => (r || "").trim());
        onSave(payload);
    };

    return (
        <div className="sc-card p-4 space-y-4" data-testid="quotation-project-charges">
            <div className="sc-section-title">Project &amp; Charges</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                    <label className="text-xs text-muted-foreground">
                        Project Name {nameLocked && <span className="ml-1 text-[10px] uppercase tracking-wide text-muted-foreground">· locked</span>}
                    </label>
                    <input
                        className="sc-input mt-1"
                        value={projectName}
                        disabled={nameLocked || !editable}
                        onChange={(e) => setProjectName(e.target.value)}
                        placeholder="Project name"
                    />
                </div>
                <div>
                    <label className="text-xs text-muted-foreground">Project Address</label>
                    <input
                        className="sc-input mt-1"
                        value={projectAddress}
                        disabled={!editable}
                        onChange={(e) => setProjectAddress(e.target.value)}
                        placeholder="Project address"
                    />
                </div>
            </div>

            <div>
                <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Other charges</div>
                    {editable && (
                        <button onClick={addCharge} className="h-7 px-2 rounded-md border border-border text-xs hover:bg-secondary inline-flex items-center gap-1">
                            <Plus className="w-3 h-3" /> Add charge
                        </button>
                    )}
                </div>
                {charges.length === 0 && <div className="text-xs text-muted-foreground">No additional charges.</div>}
                <div className="space-y-2">
                    {charges.map((c, i) => (
                        <div key={i} className="flex items-center gap-2">
                            <input
                                className="sc-input h-8 flex-1 text-sm"
                                value={c.label}
                                disabled={!editable}
                                onChange={(e) => setCharge(i, { label: e.target.value })}
                                placeholder="e.g. Delivery, Installation, Cutting, Pallet, Rental, Storage, Warehouse…"
                            />
                            <input
                                type="number"
                                step="0.01"
                                className="sc-input h-8 w-32 text-right font-mono text-sm"
                                value={c.amount}
                                disabled={!editable}
                                onChange={(e) => setCharge(i, { amount: e.target.value })}
                            />
                            {editable && (
                                <button onClick={() => removeCharge(i)} className="h-8 w-8 grid place-items-center rounded-md border border-border hover:bg-secondary" title="Remove">
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Payment terms (prints on the SQ)</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <label className="text-xs text-muted-foreground">Fully Paid Amount (no balance)</label>
                        <input type="number" step="0.01" className="sc-input mt-1 text-right font-mono" value={payFullyPaid} disabled={!editable} onChange={(e) => setPayFullyPaid(e.target.value)} placeholder="blank = fill-in line" />
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground">Partial</label>
                        <input type="number" step="0.01" className="sc-input mt-1 text-right font-mono" value={payPartial} disabled={!editable} onChange={(e) => setPayPartial(e.target.value)} placeholder="blank = fill-in line" />
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground">Balance</label>
                        <input type="number" step="0.01" className="sc-input mt-1 text-right font-mono" value={payBalance} disabled={!editable} onChange={(e) => setPayBalance(e.target.value)} placeholder="blank = fill-in line" />
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground">Tentative full-payment date of the balance</label>
                        <input type="date" className="sc-input mt-1" value={payTentative} disabled={!editable} onChange={(e) => setPayTentative(e.target.value)} />
                    </div>
                </div>
            </div>

            <div>
                <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Important reminders</div>
                    {editable && (
                        <button onClick={() => setReminders((r) => [...r, ""])} className="h-7 px-2 rounded-md border border-border text-xs hover:bg-secondary inline-flex items-center gap-1">
                            <Plus className="w-3 h-3" /> Add reminder
                        </button>
                    )}
                </div>
                {reminders.length === 0 && <div className="text-xs text-muted-foreground">Using the template's default reminders. Add one to override them on this quotation.</div>}
                <div className="space-y-2">
                    {reminders.map((r, i) => (
                        <div key={i} className="flex items-center gap-2">
                            <input
                                className="sc-input h-8 flex-1 text-sm"
                                value={r}
                                disabled={!editable}
                                onChange={(e) => setReminders((rr) => rr.map((x, j) => (j === i ? e.target.value : x)))}
                                placeholder="Reminder text…"
                            />
                            {editable && (
                                <button onClick={() => setReminders((rr) => rr.filter((_, j) => j !== i))} className="h-8 w-8 grid place-items-center rounded-md border border-border hover:bg-secondary" title="Remove">
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {editable && (
                <div className="flex justify-end">
                    <button onClick={save} className="h-9 px-4 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90" data-testid="quotation-save-header">
                        Save project &amp; charges
                    </button>
                </div>
            )}
        </div>
    );
}

function SummaryCard({ icon: Icon, label, value }) {
    return (
        <div className="sc-card p-4">
            <div className="flex items-center justify-between gap-2">
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {label}
                    </div>
                    <div className="text-lg font-semibold mt-1">{value}</div>
                </div>

                {Icon && <Icon className="w-5 h-5 text-muted-foreground" />}
            </div>
        </div>
    );
}

// Terms & Conditions: editable during the quotation (draft) phase; read-only afterwards
// with any changes highlighted vs the company template default (for approvers).
function TermsSection({ q, editable, onSaved }) {
    const tpl = useMemo(
        () => resolveQuotationTemplate({ company: q.company, branch: q.branch, displayName: q.company }),
        [q.company, q.branch]
    );
    const defaultTerms = tpl.terms || [];
    const hasOverride = Array.isArray(q.terms_override) && q.terms_override.length > 0;
    const current = hasOverride ? q.terms_override : defaultTerms;

    const [terms, setTerms] = useState(current);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        setTerms(hasOverride ? q.terms_override : defaultTerms);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q.id, JSON.stringify(q.terms_override)]);

    const defaultSet = useMemo(() => new Set(defaultTerms), [defaultTerms]);
    const overrideSet = useMemo(() => new Set(current), [current]);
    const removed = hasOverride ? defaultTerms.filter((t) => !overrideSet.has(t)) : [];
    const changedCount = hasOverride ? current.filter((t) => !defaultSet.has(t)).length + removed.length : 0;

    const save = async () => {
        setBusy(true);
        try {
            const clean = terms.map((t) => String(t).trim()).filter(Boolean);
            await api.put(`/quotations/${q.id}/terms`, { terms: clean });
            toast.success("Terms & Conditions saved");
            onSaved && onSaved();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    if (editable) {
        return (
            <div className="sc-card p-4 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="sc-section-title">
                        Terms &amp; Conditions{" "}
                        <span className="text-xs text-muted-foreground font-normal">
                            editable — changes are highlighted for approvers
                        </span>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => setTerms(defaultTerms)} className="h-8 px-3 rounded-md border border-border text-xs hover:bg-secondary">
                            Reset to template
                        </button>
                        <button onClick={() => setTerms((ts) => [...ts, ""])} className="h-8 px-3 rounded-md border border-border text-xs hover:bg-secondary">
                            + Add clause
                        </button>
                        <button onClick={save} disabled={busy} className="h-8 px-3 rounded-md bg-foreground text-background text-xs font-medium disabled:opacity-50">
                            {busy ? "Saving…" : "Save Terms"}
                        </button>
                    </div>
                </div>
                <ol className="space-y-2 list-decimal pl-5">
                    {terms.map((t, i) => {
                        const isChanged = !defaultSet.has(t);
                        return (
                            <li key={i}>
                                <div className="flex items-start gap-2">
                                    <textarea
                                        className={`sc-input text-sm flex-1 ${isChanged ? "border-amber-400 bg-amber-50" : ""}`}
                                        rows={Math.max(2, Math.ceil((t?.length || 0) / 90))}
                                        value={t}
                                        onChange={(e) => setTerms((ts) => ts.map((x, k) => (k === i ? e.target.value : x)))}
                                    />
                                    <button
                                        onClick={() => setTerms((ts) => ts.filter((_, k) => k !== i))}
                                        title="Remove clause"
                                        className="mt-1 h-8 w-8 shrink-0 rounded-md border border-border text-muted-foreground hover:bg-secondary"
                                    >
                                        ×
                                    </button>
                                </div>
                            </li>
                        );
                    })}
                </ol>
                <div className="text-xs text-muted-foreground">
                    Amber = differs from the {tpl.company?.name || "company"} template default. Blank clauses are dropped on save.
                </div>
            </div>
        );
    }

    return (
        <div className="sc-card p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="sc-section-title">Terms &amp; Conditions</div>
                {hasOverride && changedCount > 0 && (
                    <div className="text-xs flex items-center gap-3 flex-wrap">
                        <span className="inline-flex items-center gap-1">
                            <span className="inline-block w-3 h-3 rounded-sm bg-amber-200 border border-amber-400"></span>Edited / added
                        </span>
                        <span className="inline-flex items-center gap-1">
                            <span className="inline-block w-3 h-3 rounded-sm bg-rose-100 border border-rose-300"></span>Removed
                        </span>
                        <span className="text-muted-foreground">
                            {changedCount} change{changedCount === 1 ? "" : "s"} vs template
                            {q.terms_edited_by ? ` · by ${q.terms_edited_by}` : ""}
                        </span>
                    </div>
                )}
            </div>
            {hasOverride && changedCount > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-xs px-3 py-2">
                    These Terms &amp; Conditions were modified from the {tpl.company?.name || "company"} template. Review the highlighted clauses before approving.
                </div>
            )}
            <ol className="space-y-1.5 list-decimal pl-5 text-sm">
                {current.map((t, i) => {
                    const isChanged = hasOverride && !defaultSet.has(t);
                    return (
                        <li key={i} className={isChanged ? "bg-amber-100 rounded px-1" : ""}>
                            {isChanged && (
                                <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 mr-1">edited</span>
                            )}
                            {t}
                        </li>
                    );
                })}
            </ol>
            {removed.length > 0 && (
                <div>
                    <div className="text-xs font-semibold text-rose-700 mt-2 mb-1">Removed from template ({removed.length})</div>
                    <ol className="space-y-1 list-decimal pl-5 text-sm">
                        {removed.map((t, i) => (
                            <li key={i} className="text-rose-700/80 line-through">{t}</li>
                        ))}
                    </ol>
                </div>
            )}
        </div>
    );
}
